import toArray from 'lodash/toArray';
import get from 'lodash/get';

function validateMonitorNodes(caseConf) {
  const nodes = toArray(get(caseConf, 'caseLogicDescription.nodes'));

  const errors = [];
  const warnings = [];

  // Action nodes
  const actionNodes = nodes.filter(node => node.name === 'Action');

  actionNodes.forEach((node) => {
    const actionData = get(node, 'data.action');
    if (actionData) {
      if (actionData.type === 'LEADS') {
        const treatment = (caseConf._treatments || []).find(tmt => tmt.id === 'LEAD_SELECT');

        if (!treatment || !treatment.options.enabled) {
          warnings.push(monitorWarning('Lead select is used in case logic but is not enabled', 'LEAD_SELECT', undefined));
          warnings.push(actionWarning('Lead select is not enabled', node.id, 'LEAD_SELECT'));
        } else {
          const options = treatment.options.componentOptions.LEAD.availableValues;

          actionData.leads.forEach(lead => {
            if (!options.includes(lead)) {
              warnings.push(monitorWarning(`${lead} is used in case logic but is not enabled`, 'LEAD_SELECT', 'LEAD'));
              warnings.push(actionWarning(`${lead} is not enabled`, node.id, 'LEAD'));
            }
          });
        }
      }

      if (actionData.type === 'PACING') {
        if (actionData.id === 'DEFIBRILLATION' || actionData.id === 'SYNC') {
          const defibTrmt = (caseConf._treatments || []).find(tmt => tmt.id === 'DEFIBRILLATION');
          if (!defibTrmt || !defibTrmt.options.enabled) {
            warnings.push(monitorWarning('Defibrillation is used in case logic but is not enabled', 'DEFIBRILLATION', undefined));
            warnings.push(actionWarning('Defibrillation is not enabled', node.id, 'DEFIBRILLATION'));
          }
        }

        if (actionData.id === 'SYNC') {
          const syncTmnt = (caseConf._treatments || []).find(tmt => tmt.id === 'SYNC');
          if (!syncTmnt || !syncTmnt.options.enabled) {
            warnings.push(monitorWarning('Sync is used in case logic but is not enabled', 'SYNC', undefined));
            warnings.push(actionWarning('Sync is not enabled', node.id, 'SYNC'));
          }
        }

        if (actionData.id === 'TRANSCUTANEOUS') {
          const treatment = (caseConf._treatments || []).find(tmt => tmt.id === 'PACING');
          if (!treatment || !treatment.options.enabled) {
            warnings.push(monitorWarning('Pacing is used in case logic but is not enabled', 'PACING', undefined));
            warnings.push(checkWarning('Pacing is not enabled', node.id, 'PACING'));
          }
        }
      }
    }
  });

  // Check nodes
  const checkNodes = nodes.filter(node => node.name === 'Check');

  checkNodes.forEach((node) => {
    Object.entries(node.data).forEach((entry) => {
      const checkKey = entry[0];
      const check = entry[1];

      if (check.type === 'LEAD_ASSESS' && check.id === 'ECG' && check.ids) {
        const treatment = (caseConf._treatments || []).find(tmt => tmt.id === 'LEAD_SELECT');

        if (!treatment || !treatment.options.enabled) {
          warnings.push(monitorWarning('Lead select is used in case logic but is not enabled', 'LEAD_SELECT', undefined));
          warnings.push(checkWarning('Lead select is not enabled', node.id, checkKey, 'LEAD_SELECT'));
        } else {
          const options = treatment ? treatment.options.componentOptions.LEAD.availableValues : [];

          check.ids.forEach(id => {
            if (!treatment || !options.includes(id)) {
              warnings.push(monitorWarning(`${id} is used in case logic but is not enabled`, 'LEAD_SELECT', 'LEAD'));
              warnings.push(checkWarning(`${id} is not enabled`, node.id, checkKey, 'LEAD'));
            }
          });
        }
      }

      if (check.type === 'PACING' && check.id === 'TRANSCUTANEOUS') {
        const treatment = (caseConf._treatments || []).find(tmt => tmt.id === 'PACING');
        if (!treatment || !treatment.options.enabled) {
          warnings.push(monitorWarning('Pacing is used in case logic but is not enabled', 'PACING', undefined));
          warnings.push(checkWarning('Pacing is not enabled', node.id, checkKey, 'PACING'));
        } else {
          const [currMin, currMax] = treatment.options.componentOptions.CURRENT.allowedRange;
          if (check.current.min < currMin || check.current.min > currMax ||
              check.current.max < currMin || check.current.max > currMax) {
            warnings.push(monitorWarning('Current is out of range in case logic', 'PACING', 'CURRENT'));
            warnings.push(checkWarning('Current is out of range', node.id, checkKey, 'CURRENT'));
          }

          const [rateMin, rateMax] = treatment.options.componentOptions.RATE.allowedRange;
          if (check.rate.min < rateMin || check.rate.min > rateMax ||
              check.rate.max < rateMin || check.rate.max > rateMax) {
            warnings.push(monitorWarning('Rate is out of range', 'PACING', 'RATE'));
            warnings.push(checkWarning('Rate is out of range', node.id, checkKey, 'RATE'));
          }
        }
      }

      if (check.type === 'PACING' && (check.id === 'DEFIBRILLATION' || check.id === 'SYNC')) {
        const defibTrmt = (caseConf._treatments || []).find(tmt => tmt.id === 'DEFIBRILLATION');

        if (!defibTrmt || !defibTrmt.options.enabled) {
          warnings.push(monitorWarning('Defibrillation is used in case logic but is not enabled', 'DEFIBRILLATION', undefined));
          warnings.push(checkWarning('Defibrillation is not enabled', node.id, checkKey, 'DEFIBRILLATION'));
        } else {
          const [rangeMin, rangeMax] = defibTrmt.options.componentOptions.ENERGY.allowedRange;
          if (check.energy.min < rangeMin || check.energy.min > rangeMax ||
              check.energy.max < rangeMin || check.energy.max > rangeMax) {
            warnings.push(monitorWarning('Energy is out of range', 'DEFIBRILLATION', 'ENERGY'));
            warnings.push(checkWarning('Energy is out of range', node.id, checkKey, 'ENERGY'));
          }
        }

        if (check.id === 'SYNC') {
          const syncTmnt = (caseConf._treatments || []).find(tmt => tmt.id === 'SYNC');
          if (!syncTmnt || !syncTmnt.options.enabled) {
            warnings.push(monitorWarning('Sync is used in case logic but is not enabled', 'SYNC', undefined));
            warnings.push(checkWarning('Sync is not enabled', node.id, checkKey, 'SYNC'));
          }
        }
      }
    });
  });

  // Effect nodes
  const effectNodes = nodes.filter(node => node.name === 'Effect');

  effectNodes.forEach((node) => {
    const {effect} = node.data;

    if (effect && effect.type === 'VITAL' && effect.id === 'HEART_RATE' && effect.matchInput) {
      const treatment = (caseConf._treatments || []).find(tmt => tmt.id === 'PACING');
      if (!treatment || !treatment.options.enabled) {
        warnings.push(monitorWarning('Pacing is used in case logic but is not enabled', 'PACING', undefined));
        warnings.push(effectWarning('Pacing is not enabled', node.id, 'PACING'));
      }
    }
  });

  return {
    errors,
    warnings,
  };
}

function monitorWarning(message, actionId, componentId) {
  return {
    message,
    category: 'monitors',
    field: '_treatments',
    id: actionId,
    metadata: {
      componentId,
      scope: 'control',
    },
  };
}

function actionWarning(message, nodeId, componentId) {
  return {
    message,
    category: 'caseLogic',
    field: 'caseLogicDescription',
    id: nodeId,
    metadata: {
      componentId,
      scope: 'control',
    },
  };
}

function checkWarning(message, nodeId, checkKey, componentId) {
  return {
    message,
    category: 'caseLogic',
    field: 'caseLogicDescription',
    id: nodeId,
    metadata: {
      checkKey,
      componentId,
      scope: 'control',
    },
  };
}

function effectWarning(message, nodeId, componentId) {
  return {
    message,
    category: 'caseLogic',
    field: 'caseLogicDescription',
    id: nodeId,
    metadata: {
      componentId,
      scope: 'control',
    },
  };
}

export default validateMonitorNodes;
